import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const annotationDataLocationSchema = z.object({
  endOffset: z.number().int(),
  startOffset: z.number().int(),
  endContainerPath: z.array(z.number().int()),
  startContainerPath: z.array(z.number().int()),
});

export const annotationCommentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  commentText: z.string(),
  createdAtDate: z.string(),
  lastUpdatedDate: z.string(),
});

export const annotationReactionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  reactionType: z.number().int(),
  createdAtDate: z.string(),
  lastUpdatedDate: z.string(),
});

export const annotationMetadataSchema = z.object({
  pcmId: z.string(),
  userId: z.string(),
  comments: annotationCommentSchema.optional().nullable(),
  pageHref: z.string(),
  reaction: annotationReactionSchema.optional().nullable(),
  pageNumber: z.number().int(),
  createdAtDate: z.string(),
  annotationText: z.string(),
  contentVersion: z.string(),
  lastUpdatedDate: z.string(),
});

export const annotationDataSchema = z.object({
  id: z.string().optional().nullable(),
  location: annotationDataLocationSchema,
  metadata: annotationMetadataSchema,
});

const annotationSchema = z.object({
  actorId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  annotationData: z.array(annotationDataSchema),
});

const infoSchema = z.object({
  annotations: z.array(annotationSchema).optional(),
});

export type AnnotationData = z.infer<typeof annotationDataSchema>;
export type AnnotationMetadata = z.infer<typeof annotationMetadataSchema>;
export type AnnotationComment = z.infer<typeof annotationCommentSchema>;
export type AnnotationReaction = z.infer<typeof annotationReactionSchema>;
export type Annotation = z.infer<typeof annotationSchema>;
export type AnnotationLocation = z.infer<typeof annotationDataLocationSchema>;

export type InfoResponse = z.infer<typeof infoSchema>;

export type InfoType = 'annotations';

export interface UseInfoApiProps {
  infoType: InfoType;
  orgId: string;
  teacherId: string;
  classId?: string;
  studentId?: string;
  contentId: string;
  products?: string[];
}

const getInfoApi = ({
  infoType,
  orgId,
  teacherId,
  classId,
  studentId,
  contentId,
  products,
}: UseInfoApiProps) =>
  fetcher('/info', {
    queryParameters: {
      infoType,
      orgId,
      teacherId,
      classId,
      studentId,
      contentId,
      products: products?.join(','),
    },
  }).then((res) => infoSchema.parse(res));

export function useInfoApi({
  infoType,
  orgId,
  teacherId,
  classId,
  studentId,
  contentId,
  products,
}: UseInfoApiProps): UseQueryResult<InfoResponse, Error> {
  return useQuery({
    queryFn: () =>
      getInfoApi({
        infoType,
        orgId,
        teacherId,
        classId,
        studentId,
        contentId,
        products,
      }),
    queryKey: [
      infoType,
      orgId,
      teacherId,
      classId,
      studentId,
      contentId,
      products,
    ].filter(Boolean),
    enabled: !!orgId && !!teacherId && !!contentId,
  });
}
